export const comparisons = {
  nav: 'Compare',
  received: 'Received agreement',
  standard: 'Standard',
  project: 'Project',
  by: 'By',
  date: 'Date',
  result: 'Result',
  version: (n: number) => `version ${n}`,
  statuses: { queued: 'Waiting…', processing: 'Comparing…', ready: 'Ready', failed: 'Failed' } as Record<string, string>,
  compare: 'Compare a document',
  ourRole: 'We are the',
  roles: { auto: 'Work it out from the standard', customer: 'Customer / buyer', supplier: 'Supplier / seller', discloser: 'Disclosing party', recipient: 'Receiving party', mutual: 'Both (mutual)' } as Record<string, string>,
  focus: 'Anything to focus on? (optional)',
  focusPlaceholder: 'e.g. liability and data protection matter most for this supplier',
  start: 'Compare',
  starting: 'Starting…',
  startFailed: 'Could not start the comparison',
  unsupportedFile: 'Only Word (.docx) and PDF files can be compared',
  tooLarge: 'The file is larger than 25 MB',
  // Standards tab
  prep: {
    pending: 'Not prepared yet',
    preparing: 'Preparing…',
    ready: (n: number | null) => (n ? `Ready · ${n} clauses` : 'Ready'),
    failed: 'Couldn’t be read',
  } as { pending: string; preparing: string; ready: (n: number | null) => string; failed: string },
  status: 'Status',
  // Comparison page
  back: 'Compare',
  progressTitle: 'Comparing the agreements',
  progressHint: 'This usually takes about a minute. You can leave this page; the result is saved in the project.',
  stages: {
    preparing: 'Reading both documents',
    aligning: 'Matching clauses',
    analysing: 'Analysing clauses',
    summarising: 'Summarising',
  } as Record<string, string>,
  failedTitle: 'The comparison could not be completed',
  retry: 'Run again',
  overall: { close: 'Close to our standard', moderate: 'Some deviations to negotiate', far: 'Far from our standard' } as Record<string, string>,
  counts: (c: { high: number; medium: number; low: number }) => `${c.high} high · ${c.medium} medium · ${c.low} low`,
  incomplete: 'Part of the analysis failed, so some points may be missing. Run it again for a complete result.',
  topDeviations: 'Most important',
  nextSteps: 'Suggested next steps',
  downloadRedline: 'Download redline',
  download: 'Download',
  redlineShort: 'Redline (tracked changes)',
  cleanShort: 'Clean version',
  reportShort: 'Report',
  cleanHint: 'Word document with the accepted suggestions applied as plain text: no tracked changes, no comments',
  reportHint: 'Word report of all findings',
  acceptedBar: (n: number) => `${n} suggestion${n === 1 ? '' : 's'} accepted`,
  redlineHint: 'Word document with the accepted suggestions as tracked changes and comments in your name',
  redlinePdf: 'A redline needs the received agreement as a Word document. Download the report instead.',
  needAccepted: 'Accept the suggestions you want applied first',
  acceptAll: 'Accept all high and medium',
  exported: (name: string) => `${name} downloaded`,
  exportFailed: 'Could not create the document',
  skipped: (n: number) => `${n} suggestions could not be placed in the document and are listed in the report only`,
  rerun: 'Run again',
  rerunConfirm: 'Run the comparison again? Accepted and dismissed choices are kept on the current result only.',
  filters: { all: 'All', high: 'High', medium: 'Medium', low: 'Low', missing: 'Missing in theirs', added: 'Added by them', open: 'Open' } as Record<string, string>,
  findingsTitle: 'Findings',
  noFindings: 'No deviations in this filter.',
  kinds: { changed: 'Changed', missing: 'Missing in theirs', added: 'Added by them', match: 'Matches' } as Record<string, string>,
  directions: { stricter: 'worse for us', looser: 'better for us', neutral: 'neutral' } as Record<string, string>,
  risks: { high: 'High', medium: 'Medium', low: 'Low', none: '-' } as Record<string, string>,
  ourStandard: 'Our standard',
  theirText: 'Their text',
  suggestion: 'Suggested change',
  suggestionKinds: { edit: 'Replace with', insert: 'Insert', delete: 'Delete' } as Record<string, string>,
  accept: 'Accept',
  accepted: 'Accepted',
  dismiss: 'Dismiss',
  dismissed: 'Dismissed',
  reopen: 'Undo',
  editWording: 'Edit wording',
  saveWording: 'Save',
  cancel: 'Cancel',
  lowConfidence: 'Uncertain: check this one',
  viewOnly: 'You can view this comparison; only editors of the project can accept or dismiss findings.',
  matchesTitle: (n: number) => `Matches our standard (${n})`,
  clauseRefs: (theirs: string | null, ours: string | null) => [theirs && `Their ${theirs}`, ours && `our ${ours}`].filter(Boolean).join(' · '),
  updateFailed: 'Could not save',
  deleteComparison: 'Delete comparison',
  deleted: 'Comparison deleted',
  // Entry points: project file, chat, continue in chat
  compareFile: 'Compare with standard…',
  fileDialogTitle: (file: string) => `Compare “${file}” with a standard`,
  fileDialogDescription: 'Queri compares this document clause by clause with the standard you pick and marks what to negotiate. The file stays where it is.',
  pickStandard: 'Standard agreement',
  pickStandardPlaceholder: 'Choose a standard',
  noStandards: 'Your organization has no standard agreements yet. Add one under Playbooks → Standard agreements.',
  standardsFailed: 'Could not load the standard agreements',
  continueInChat: 'Continue in chat',
  openingChat: 'Opening chat…',
  chatFailed: 'Could not open the chat',
  openComparison: 'Open comparison',
  cardTitle: (standard: string) => `Comparison with “${standard}”`,
  cardUnavailable: 'This comparison is no longer available.',
  cardFailed: 'The comparison could not be completed.',
  cardFile: (file: string) => `Received: ${file}`,
  chatBanner: 'You are discussing a comparison. Ask for changes to the proposed wording and they appear as suggestions on the received agreement.',
  chatBannerLink: 'Open comparison',
  chatStarted: (file: string, standard: string) => `Compare “${file}” with the standard “${standard}”`,
  // Our standards (library on the Compare page)
  lib: {
    intro: 'Your organisation’s own contract templates. Compare checks agreements you receive against them, clause by clause.',
    title: 'Our standard agreements',
    upload: 'Upload',
    empty: 'No standard agreements yet',
    emptyHint: 'Upload your templates (Word or PDF) so everyone in your organisation works from the same standard.',
    loadFailed: 'Failed to load standard agreements',
    name: 'Name',
    agreementType: 'Agreement type',
    addedBy: 'Added by',
    added: 'Added',
    download: 'Download',
    edit: 'Edit details',
    delete: 'Delete',
    downloadFailed: 'Failed to download the file',
    uploadDialogTitle: 'Upload standard agreement',
    uploadDialogDescription: 'Word (.docx) or PDF, up to 25 MB. Everyone in your organisation will see it.',
    chooseFile: 'Choose a file or drop it here',
    fileTypes: '.docx or .pdf, up to 25 MB',
    namePlaceholder: 'e.g. Standard SaaS Agreement',
    typePlaceholder: 'e.g. SaaS, NDA, MSA',
    descriptionLabel: 'Notes (optional)',
    descriptionPlaceholder: 'When to use it, version, owner…',
    cancel: 'Cancel',
    save: 'Save',
    uploading: 'Uploading…',
    uploaded: (name: string) => `"${name}" added`,
    uploadFailed: 'Upload failed',
    unsupportedFile: 'Only Word (.docx) and PDF files are supported',
    tooLarge: 'The file is larger than 25 MB',
    editDialogTitle: 'Edit standard agreement',
    saved: 'Saved',
    saveFailed: 'Could not save the changes',
    deleteTitle: 'Delete standard agreement',
    deleteDescription: (name: string) =>
      `Delete "${name}" for everyone in your organisation? The file is removed and this cannot be undone.`,
    deleted: 'Standard agreement deleted',
    deleteFailed: 'Could not delete the standard agreement',
    openMenu: 'Open menu',
    moreActions: 'More actions',
  },
  // Compare page
  cp: {
    title: 'Compare',
    subtitle: 'Check an agreement you received against one of your organization’s standard agreements.',
    dropTitle: 'Drop the agreement here',
    dropHint: 'Word or PDF, up to 25 MB. Click to choose a file.',
    dropAria: 'Drop the agreement to compare here, or press Enter to choose a file. Word or PDF.',
    dropRelease: 'Release to use this file',
    tabCompare: 'Compare',
    tabStandards: 'Our standards',
    chosenFile: 'Agreement you received',
    step1Title: 'Our standard agreement',
    step1Hint: 'The reference to compare against. Only standards that are ready can be chosen.',
    step2Title: 'Agreement you received',
    step2Label: 'The agreement you received from the other party',
    step3Title: 'Our role and options',
    standardsEmpty: 'No standard agreements yet. Upload your organisation’s standard to compare against.',
    uploadFirst: 'Upload a standard agreement',
    uploadNew: 'Upload a new standard',
    uploadingStandard: 'Uploading…',
    manageStandards: 'Manage standards',
    retry: 'Retry',
    retried: 'Preparing again…',
    retryFailed: 'Could not restart the preparation',
    reasonChoose: 'Choose a ready standard',
    reasonWaiting: (name: string) => `Waiting for “${name}” to be prepared…`,
    reasonFailed: (name: string) => `“${name}” couldn’t be read. Retry or choose another standard`,
    reasonFile: 'Add the agreement you received',
    looksLikeStandard: (name: string) => `This looks like your standard agreement “${name}”. Compare needs the agreement you received.`,
    uploadAsStandard: 'Upload it as a standard instead',
    compareAnyway: 'Compare anyway',
    standardsLabel: 'Standard agreement',
    changeFile: 'Choose another file',
    removeFile: 'Remove the file',
    orPick: 'or',
    pickFromProject: 'Pick a file from a project',
    pickerTitle: 'Pick a document from a project',
    pickerDescription: 'The file stays in its project. The comparison is still private to you.',
    pickerProject: 'Project',
    pickerProjectPlaceholder: 'Choose a project',
    pickerNoProjects: 'You have no projects with Word or PDF files yet.',
    pickerNoFiles: 'This project has no Word or PDF files.',
    pickerFile: 'Document',
    pickerUse: 'Use this document',
    pickerFailed: 'Could not load the projects',
    suggested: 'suggested',
    chooseStandard: 'Choose a standard',
    weAre: 'We are the',
    options: 'Options',
    run: 'Compare',
    quickTitle: 'Quick checks',
    quickSubtitle: 'Your saved compare workflows. Drop a document on one to run it straight away.',
    quickDrop: 'Drop a document here or click to choose',
    quickAria: (name: string) => `${name}: drop a document or press Enter to choose a file and run this check`,
    quickStandard: (name: string) => `Against ${name}`,
    quickNoStandard: 'This workflow needs a ready standard agreement. Edit it under Playbooks → Workflows.',
    quickRunning: 'Starting…',
    mineTitle: 'My comparisons',
    mineSubtitle: 'Only you can see your comparisons.',
    mineEmpty: 'No comparisons yet. Drop an agreement above to run your first.',
    mineFailed: 'Could not load your comparisons',
    showMore: (n: number) => `Show ${n} more`,
    open: 'Open',
    standardGone: 'The standard agreement or the file is no longer available.',
    compareAnother: 'Compare another document',
    compareAnotherHint: 'Opens Compare with the same standard, role and focus filled in',
  },
};

export type ComparisonsDict = typeof comparisons;
